/**
 * OnboardingTaskStep Component
 * Third step: Create a task
 */

import { useState } from 'react'
import { toast } from 'sonner'
import { TaskForm, TaskFormData } from '@/components/tasks/TaskForm'
import { taskService } from '@/services'
import { useAuth } from '@/hooks/useAuth'
import { useOnboarding } from '@/contexts/OnboardingContext.context'
import { Status, TaskType, Priority, Horizon } from '@/types/enums'

interface OnboardingTaskStepProps {
  onSkip: () => void
  skipLabel?: string
}

export function OnboardingTaskStep({ onSkip }: OnboardingTaskStepProps) {
  const { user } = useAuth()
  const { workspaceId, projectId, setTaskName, completeOnboarding } = useOnboarding()
  const [isLoading, setIsLoading] = useState(false)

  const onSubmit = async (data: TaskFormData) => {
    if (!user) {
      toast.error('You must be logged in to create a task')
      return
    }

    if (!workspaceId || !projectId) {
      toast.error('Workspace and project are required')
      return
    }

    setIsLoading(true)
    try {
      const createData = {
        type: TaskType.Task,
        name: data.name,
        description: data.description,
        workspaceId,
        projectIds: [projectId],
        assigneeId: user.id, // Assign task to the current user
        status: Status.ToDo,
        ...(data.priority && { priority: data.priority }),
        ...(data.horizon && { horizon: data.horizon }),
      }

      await taskService.createTask(createData, user.id)

      setTaskName(data.name)
      toast.success('Task created successfully!')
      completeOnboarding()
    } catch (error) {
      console.error('Failed to create task:', error)
      toast.error('Failed to create task')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <TaskForm
      mode="create"
      initialData={{
        type: TaskType.Task,
        name: '',
        description: '',
        workspaceId: workspaceId || '',
        status: Status.ToDo,
        priority: Priority.None,
        horizon: Horizon.None,
      }}
      onSubmit={onSubmit}
      onCancel={onSkip}
      loading={isLoading}
      submitText={isLoading ? 'Creating Task...' : 'Create Task & Get Started'}
      cancelText="Skip Task Creation"
      title="Create Your First Task"
      description="Tasks are the building blocks of your projects — create one to get started!"
      simplified={true}
    />
  )
}
